/**
 * SharkMaterial — the hand-rolled underwater material every voxel creature wears:
 * vertex-stage spine swim deformation (SPEC §4.3) plus a half-lambert / hemispheric /
 * rim / dapple / fog fragment pass, compiled once for Mesh and once for InstancedMesh.
 */

import * as THREE from 'three';
import vertexShader from './shaders/shark.vert.glsl?raw';
import fragmentShader from './shaders/shark.frag.glsl?raw';
import { SWIM, WATER } from '../data/tuning.ts';
import type { JawRig } from '../voxel/MeshService.ts';
import { lureUniforms } from './LureLight.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

export interface SharkMaterialOptions {
  /** compile the INSTANCED path: per-instance `aPhase` attribute instead of the `uPhase` uniform */
  instanced?: boolean;
  /** compile the JAW path: mandible / rostrum / head-shake stages driven by `jawGroup` */
  jaw?: boolean;
}

/**
 * Look constants the WATER contract does not carry. They describe this one
 * material rather than a stratum, so they live here instead of in tuning.ts.
 */
// SPEC §10.1: "Apply caustics to the shark's dorsal surface too — light dapples over the
// player. This one detail sells the whole thing." It is also the main thing separating a
// dark countershaded back from dark water, so it is pitched well above a subtle accent.
const DAPPLE_STRENGTH = 1.35;

/**
 * World length the spineT 0->1 range spans, used only to turn the wave's
 * d/dSpineT slope into a real surface gradient for the normal. A wrong value
 * merely over- or under-tilts the shading. 7.0 u ≈ a 38-voxel shark at
 * VOXEL.size; `setSpineLength(mesh.size.z)` makes it exact.
 */
const DEFAULT_SPINE_LENGTH = 7.0;

/** sRGB hex token -> a Color in the linear working space the shaders expect. */
function srgb(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
}

export class SharkMaterial extends THREE.ShaderMaterial {
  /** true when compiled with the INSTANCED define; clone() must preserve it */
  readonly instanced: boolean;

  /** true when compiled with the JAW define — the articulated head stages are live */
  readonly jawed: boolean;

  constructor(opts: SharkMaterialOptions = {}) {
    const instanced = opts.instanced === true;
    const jawed = opts.jaw === true;
    const defines: Record<string, string> = {};
    // '' expands to a bare `#define X`, which is all the shader tests for.
    if (instanced) defines.INSTANCED = '';
    if (jawed) defines.JAW = '';
    super({
      name: 'SharkMaterial',
      vertexShader,
      fragmentShader,
      defines,
      // Every uniform is declared here: a uniform added after the program links
      // is silently ignored for the life of that program.
      uniforms: {
        ...lureUniforms(),
        uTime: { value: 0 },
        // Integrated undulation phase. See setTime(): this is advanced by rate*dt rather
        // than recomputed as elapsed*rate, so changing swim speed never jumps the wave.
        uOm: { value: 0 },
        uFrequency: { value: SWIM.frequency },
        uSpeed: { value: SWIM.speedIdle },
        uAmp: { value: SWIM.ampIdle },
        uStiffness: { value: SWIM.stiffness },
        uPhase: { value: 0 },
        uSpineLength: { value: DEFAULT_SPINE_LENGTH },
        // Body coordinate remap: u = spineT*uUScale + uUBias, clamped to 1.45 in the shader.
        // These reproduce the source model's u = (x - 0.08)/0.80 over its 1.24-unit span,
        // and the tail tip lands exactly on the 1.45 ceiling.
        uUScale: { value: 1.55 },
        uUBias: { value: -0.10 },
        uTwistGain: { value: 0.55 },
        uCurve: { value: 0 },

        // Jaw rig. Always declared — a uniform added after the program links is dead — but
        // only read under the JAW define. uShakeZ far downrange keeps the shake stage inert
        // until a real rig arrives.
        uJaw: { value: 0 },
        uSnout: { value: 0 },
        uShake: { value: 0 },
        uSnoutEndZ: { value: 0 },
        uShakeZ: { value: 1e9 },
        uHingeA: { value: new THREE.Vector2(0, 0) },
        uHingeB: { value: new THREE.Vector2(0, 0) },

        uSunDir: {
          value: new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize(),
        },
        uSunColor: { value: srgb(WATER.sunColor) },
        uAmbientTop: { value: srgb(WATER.ambientTop) },
        uAmbientBottom: { value: srgb(WATER.ambientBottom) },
        uRimColor: { value: srgb(WATER.rimColor) },
        uFogColor: { value: srgb(WATER.fogColor) },
        uFogDensity: { value: WATER.fogDensity },
        uDappleStrength: { value: DAPPLE_STRENGTH },
        uFlash: { value: 0 },

        // Shadow receiving. 0 strength until a pass is attached, so the material renders
        // correctly on its own — and note the depth twin shares this uniforms object by
        // reference, which is harmless: its fragment shader declares none of these, and
        // three only uploads what the linked program actually asks for.
        uShadowMap: { value: null as THREE.Texture | null },
        uShadowMatrix: { value: new THREE.Matrix4() },
        uShadowStrength: { value: 0 },
      },
      vertexColors: true,
      side: THREE.FrontSide,
      transparent: false,
      // We do our own exp2 fog against vViewDepth; leaving three's fog off keeps
      // it from injecting a second, conflicting fog block.
      fog: false,
    });
    this.instanced = instanced;
    this.jawed = jawed;
  }

  /**
   * A depth-only twin for the shadow pass, running the SAME vertex program — wave, bend,
   * jaw, instancing — so the shadow matches the deformed pose rather than the bind pose.
   *
   * The uniforms object is shared BY REFERENCE, not copied: the twin is therefore always
   * exactly in phase with its parent with no per-frame syncing, and cannot drift.
   */
  makeShadowMaterial(): THREE.ShaderMaterial {
    const m = new THREE.ShaderMaterial({
      name: 'SharkMaterial:depth',
      vertexShader,
      // Colour is irrelevant; only the depth buffer is read back.
      fragmentShader: 'void main() { gl_FragColor = vec4(1.0); }',
      defines: { ...(this.defines as Record<string, string>) },
      uniforms: this.uniforms,
      vertexColors: true,
      side: THREE.FrontSide,
      fog: false,
    });
    return m;
  }

  /** install a model's articulation, in local mesh units. Null parks the rig inert. */
  setJawRig(rig: JawRig | null | undefined): void {
    const u = this.uniforms;
    // Undefined as well as null: an older worker build predates the field entirely, and a
    // stale module in dev should park the rig, not throw inside the build promise.
    if (!rig) {
      u.uShakeZ.value = 1e9;
      u.uSnoutEndZ.value = 0;
      u.uJaw.value = 0;
      u.uSnout.value = 0;
      u.uShake.value = 0;
      return;
    }
    (u.uHingeA.value as THREE.Vector2).set(rig.hingeLowerZ, rig.hingeLowerY);
    (u.uHingeB.value as THREE.Vector2).set(rig.hingeUpperZ, rig.hingeUpperY);
    u.uSnoutEndZ.value = rig.snoutEndZ;
    u.uShakeZ.value = rig.shakeZ;
  }

  /**
   * Drive the bite. `jaw` drops the mandible, `snout` lifts the rostrum, `shake` yaws the
   * head about the pectoral girdle — all radians.
   */
  /**
   * Steady lateral bend, in world units of tail offset. Positive curves the tail toward
   * local +X. This is the turn held in the body, separate from the travelling stroke.
   */
  setCurve(c: number): void {
    this.uniforms.uCurve.value = c;
  }

  setBite(jaw: number, snout: number, shake: number): void {
    this.uniforms.uJaw.value = jaw;
    this.uniforms.uSnout.value = snout;
    this.uniforms.uShake.value = shake;
  }

  /** wall-clock of the last setTime, for integrating the phase; -1 until the first call */
  private lastTime = -1;
  private om = 0;

  /**
   * Per-frame. Drives the dapple scroll from absolute time, and INTEGRATES the undulation
   * phase from the current rate — the rate changes with speed, and phase has to be
   * continuous across that change or the body judders every time the animal accelerates.
   */
  setTime(t: number): void {
    const dt = this.lastTime < 0 ? 0 : Math.min(Math.max(t - this.lastTime, 0), 0.25);
    this.lastTime = t;
    this.uniforms.uTime.value = t;
    // Wrapped at 2pi so the accumulator never grows large enough to lose float precision
    // in the shader; the wave has period 2pi so the wrap is invisible.
    this.om = (this.om + (this.uniforms.uSpeed.value as number) * dt) % (Math.PI * 2);
    this.uniforms.uOm.value = this.om;
  }

  /** amp in world units at the tail (spineT = 1), undulation rate in rad/s */
  setSwim(amp: number, speed: number): void {
    this.uniforms.uAmp.value = amp;
    this.uniforms.uSpeed.value = speed;
  }

  /** phase offset in radians; ignored when compiled INSTANCED (aPhase wins) */
  setPhase(p: number): void {
    this.uniforms.uPhase.value = p;
  }

  /**
   * World length spanned by spineT 0->1. Affects only how hard the normal tilts
   * with the wave. `SharkMesh.size.z` is the exact value for a given mesh.
   */
  setSpineLength(len: number): void {
    this.uniforms.uSpineLength.value = len > 0.01 ? len : 0.01;
  }

  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.uniforms, env);
  }

  /** pull sun / fog / ambient from a WATER-shaped config (data/tuning.ts) */
  applyWaterConfig(cfg: typeof WATER): void {
    const u = this.uniforms;
    (u.uSunDir.value as THREE.Vector3)
      .set(cfg.sunDir[0], cfg.sunDir[1], cfg.sunDir[2])
      .normalize();
    (u.uSunColor.value as THREE.Color).setHex(cfg.sunColor, THREE.SRGBColorSpace);
    (u.uAmbientTop.value as THREE.Color).setHex(cfg.ambientTop, THREE.SRGBColorSpace);
    (u.uAmbientBottom.value as THREE.Color).setHex(cfg.ambientBottom, THREE.SRGBColorSpace);
    (u.uRimColor.value as THREE.Color).setHex(cfg.rimColor, THREE.SRGBColorSpace);
    (u.uFogColor.value as THREE.Color).setHex(cfg.fogColor, THREE.SRGBColorSpace);
    u.uFogDensity.value = cfg.fogDensity;
  }

  /** point this material at a shadow pass; `strength` 0..1 fades the whole effect */
  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    const u = this.uniforms;
    u.uShadowMap.value = map;
    (u.uShadowMatrix.value as THREE.Matrix4).copy(matrix);
    u.uShadowStrength.value = map === null ? 0 : strength;
  }

  /** 0..1 mix toward white for hit feedback */
  setFlash(v: number): void {
    this.uniforms.uFlash.value = v < 0 ? 0 : v > 1 ? 1 : v;
  }

  /**
   * Material.clone() would call `new SharkMaterial()` with no options and lose the
   * INSTANCED define before copy() could restore the field, so construct it directly.
   */
  override clone(): this {
    const copy = new SharkMaterial({ instanced: this.instanced, jaw: this.jawed });
    copy.copy(this);
    return copy as this;
  }
}

/**
 * Map current speed to swim params (SPEC §4.3): a stationary shark barely
 * undulates, a sprinting one thrashes. Linear on clamp(speed / cruise, 0, 1).
 */
export function swimParamsForSpeed(speed: number, cruise: number): { amp: number; speed: number } {
  const raw = cruise > 0 ? speed / cruise : 0;
  const t = raw < 0 ? 0 : raw > 1 ? 1 : raw;
  return {
    amp: SWIM.ampIdle + (SWIM.ampCruise - SWIM.ampIdle) * t,
    speed: SWIM.speedIdle + (SWIM.speedCruise - SWIM.speedIdle) * t,
  };
}
